import React, { useState, useEffect } from "react";
import { Typography, TextField } from "@mui/material";
import FadeSlider from '../sliders/FadeSlider';
import BlueSlider from '../sliders/BlueSlider';

export type PatternFilterProps = {
    paintSeed: number | null;
    minFade: number;
    maxFade: number;
    minBlue: number;
    maxBlue: number;
    onChange: (partial: Record<string, number | null>) => void;
};

const PatternFilter: React.FC<PatternFilterProps> = ({
                                                         paintSeed,
                                                         minFade,
                                                         maxFade,
                                                         minBlue,
                                                         maxBlue,
                                                         onChange,
                                                     }) => {
    const [open, setOpen] = useState(false);

    // Lokale Werte (Smooth)
    const [localPaint, setLocalPaint] = useState(paintSeed ?? "");
    const [localFadeMin, setLocalFadeMin] = useState(minFade);
    const [localFadeMax, setLocalFadeMax] = useState(maxFade);
    const [localBlueMin, setLocalBlueMin] = useState(minBlue);
    const [localBlueMax, setLocalBlueMax] = useState(maxBlue);

    // Sync wenn Filter resettet wird
    useEffect(() => {
        setLocalPaint(paintSeed ?? "");
        setLocalFadeMin(minFade);
        setLocalFadeMax(maxFade);
        setLocalBlueMin(minBlue);
        setLocalBlueMax(maxBlue);
    }, [paintSeed, minFade, maxFade, minBlue, maxBlue]);

    // Trigger API for Fade while typing with debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            onChange({ minFade: localFadeMin, maxFade: localFadeMax });
        }, 300);

        return () => clearTimeout(timer);
    }, [localFadeMin, localFadeMax]);

    // Trigger API for Blue while typing with debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            onChange({ minBlue: localBlueMin, maxBlue: localBlueMax });
        }, 300);

        return () => clearTimeout(timer);
    }, [localBlueMin, localBlueMax]);

    // Trigger API for Paint Seed while typing with debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            onChange({
                paintSeed:
                    localPaint === "" ? null : Math.max(1, Math.min(Number(localPaint), 1000)),
            });
        }, 300);

        return () => clearTimeout(timer);
    }, [localPaint]);

    const textFieldSx = {
        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
            display: 'none',
            margin: 0,
        },
        '& input[type=number]': {
            MozAppearance: 'textfield',
        },
        '& .MuiInputBase-input': {
            fontSize: '0.80rem',
            fontWeight: 600,
        },
        '& .MuiInputLabel-root': {
            fontSize: '0.80rem',
            fontWeight: 600,
        },
    };

    return (
        <div className="border border-purple-700/50 rounded-md overflow-hidden">

            <button
                onClick={() => setOpen(!open)}
                className={`w-full flex justify-between items-center px-3 py-2 text-gray-300 text-sm transition ${
                    open ? "bg-purple-900/40" : ""
                }`}
            >
                <span className="!text-[0.80rem] !font-semibold">Muster</span>
                <span className="!text-[0.80rem] !font-semibold">{open ? "▲" : "▼"}</span>
            </button>

            <div
                className={`transition-all duration-300 overflow-hidden ${
                    open ? "max-h-[9999px]" : "max-h-0"
                }`}
            >
                <div className="px-3 pb-4 pt-2 space-y-4">

                    {/* PAINT SEED */}
                    <div>
                        <Typography className="text-gray-300 !text-[0.80rem] !font-semibold">Paint Seed</Typography>

                        <TextField
                            size="small"
                            fullWidth
                            type="number"
                            value={localPaint}
                            onChange={(e) => setLocalPaint(e.target.value)}
                            sx={textFieldSx}
                        />
                    </div>

                    {/* FADE */}
                    <div>
                        <Typography className="text-gray-300 !text-[0.80rem] !font-semibold">Fade %</Typography>

                        <FadeSlider
                            value={[localFadeMin, localFadeMax]}
                            onChange={(a, b) => {
                                setLocalFadeMin(Math.max(0, a));
                                setLocalFadeMax(Math.max(0, b));
                            }}
                            onCommit={() => {}}
                        />

                        <div className="flex gap-2 mt-2">
                            <TextField
                                size="small"
                                label="Min"
                                type="number"
                                value={localFadeMin}
                                onChange={(e) => setLocalFadeMin(Math.max(0, Number(e.target.value)))}
                                className="flex-1"
                                sx={textFieldSx}
                            />

                            <TextField
                                size="small"
                                label="Max"
                                type="number"
                                value={localFadeMax}
                                onChange={(e) => setLocalFadeMax(Math.max(0, Number(e.target.value)))}
                                className="flex-1"
                                sx={textFieldSx}
                            />
                        </div>
                    </div>

                    {/* BLUE */}
                    <div>
                        <Typography className="text-gray-300 !text-[0.80rem] !font-semibold">Blue %</Typography>

                        <BlueSlider
                            value={[localBlueMin, localBlueMax]}
                            onChange={(a, b) => {
                                setLocalBlueMin(Math.max(0, a));
                                setLocalBlueMax(Math.max(0, b));
                            }}
                            onCommit={() => {}}
                        />

                        <div className="flex gap-2 mt-2">
                            <TextField
                                size="small"
                                label="Min"
                                type="number"
                                value={localBlueMin}
                                onChange={(e) => setLocalBlueMin(Math.max(0, Number(e.target.value)))}
                                className="flex-1"
                                sx={textFieldSx}
                            />

                            <TextField
                                size="small"
                                label="Max"
                                type="number"
                                value={localBlueMax}
                                onChange={(e) => setLocalBlueMax(Math.max(0, Number(e.target.value)))}
                                className="flex-1"
                                sx={textFieldSx}
                            />
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default PatternFilter;